import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { labelFor } from "@/components/art/ArtMockup";

// All purchase-option state for the artwork page: poster sizes + frames, or Gelato material sizes.
export const useArtworkOptions = (id) => {
  const { add } = useCart();
  const [art, setArt] = useState(null);
  const [size, setSize] = useState(null);
  const [frames, setFrames] = useState([]);
  const [frame, setFrame] = useState(null);
  const [materials, setMaterials] = useState([]); // [{material, label, description, starts_at_usd, sizes}]
  const [material, setMaterial] = useState("poster");
  const [materialSizes, setMaterialSizes] = useState([]); // non-poster: [{size, retail_price_usd, orientation, product_uid}]
  const [materialSizeSel, setMaterialSizeSel] = useState(null);
  const [materialFrameColor, setMaterialFrameColor] = useState("black"); // framed materials only

  useEffect(() => {
    api.get(`/artworks/${id}`).then(r => {
      setArt(r.data);
      if (r.data.sizes?.length) setSize(r.data.sizes[0]);
    });
    api.get(`/prints/materials?artwork_id=${encodeURIComponent(id)}`).then(r => setMaterials(r.data.materials || [])).catch(() => setMaterials([]));
  }, [id]);

  // Frame options follow the poster size
  useEffect(() => {
    setFrame(null);
    setFrames([]);
    if (!art || !art.prints_only || !size || material !== "poster") return;
    api.get(`/prints/frames-for/${encodeURIComponent(size.size)}`)
      .then(r => setFrames(r.data.options || []))
      .catch(() => setFrames([]));
  }, [art, size, material]);

  // Non-poster material sizes follow the material
  useEffect(() => {
    if (material === "poster") { setMaterialSizes([]); setMaterialSizeSel(null); return; }
    api.get(`/prints/materials/${material}/sizes?artwork_id=${encodeURIComponent(id)}`)
      .then(r => {
        const opts = r.data.options || [];
        setMaterialSizes(opts);
        setMaterialSizeSel(opts[0] || null);
      })
      .catch(() => { setMaterialSizes([]); setMaterialSizeSel(null); });
  }, [material, id]);

  // Framed materials price per frame colour; fall back to the first colour offered for that size
  const colorOption = useMemo(() => {
    const opts = materialSizeSel?.frame_colors || [];
    return opts.find(c => c.frame_color === materialFrameColor) || opts[0] || null;
  }, [materialSizeSel, materialFrameColor]);
  const materialUnitPrice = colorOption ? colorOption.retail_price_usd : materialSizeSel?.retail_price_usd;
  const activeSize = useMemo(() => material === "poster"
    ? size
    : (materialSizeSel ? { size: materialSizeSel.size, price: materialUnitPrice } : null),
  [material, size, materialSizeSel, materialUnitPrice]);
  const totalPrice = useMemo(() => {
    if (!activeSize) return 0;
    return Number(activeSize.price) + (material === "poster" && frame?.upsell_usd ? frame.upsell_usd : 0);
  }, [activeSize, frame, material]);
  const currentMaterialMeta = useMemo(() => materials.find(m => m.material === material), [materials, material]);

  const handleAdd = () => {
    if (!activeSize) { toast.error("Please select a size"); return false; }
    if (material === "poster") {
      add(art, size, frame);
    } else {
      add(art, { size: materialSizeSel.size, price: materialUnitPrice }, colorOption ? { frame_color: colorOption.frame_color } : null,
          { material, label: currentMaterialMeta?.label || material, retail_price_usd: materialUnitPrice });
    }
    const frameLabel = material === "poster" ? frame?.frame_color : colorOption?.frame_color;
    toast.success(`${art.title} · ${activeSize.size} · ${currentMaterialMeta?.label || "Museum Paper"}${frameLabel ? " · " + labelFor(frameLabel) : ""} added to cart`);
    return true;
  };

  return {
    art, size, setSize, frames, frame, setFrame, materials, material, setMaterial, materialSizes, materialSizeSel, setMaterialSizeSel,
    activeSize, totalPrice, currentMaterialMeta, handleAdd,
    materialFrameColor: colorOption?.frame_color || materialFrameColor, setMaterialFrameColor,
    frameColorOptions: materialSizeSel?.frame_colors || [],
    soldOut: !!(art?.is_sold && !art?.prints_only),
  };
};
